import { FOLLOW_UP_DAYS, followUpFromWorkbook, parseEvaluation, type EvaluationCode } from "./tasting";
import { addDays, civilToday } from "./time";
import { cellText } from "./workbook";

/** Stages that never need a follow-up call. */
export const FOLLOW_UP_EXCLUDED_STAGES = ["lost", "cancelled", "confirmed", "completed"] as const;
export const FOLLOW_UP_HORIZON_DAYS = 7;
export const SNOOZE_DAYS = [1, 3, 7] as const;

const RANK: Record<EvaluationCode, number> = { TP: 0, HP: 1, MP: 2, LP: 3, VLP: 4, OTHER: 5 };

/** One events row with the latest open tasting and the newest contact traces. */
export type FollowUpSource = {
  id: string;
  title: string;
  sales_stage: string;
  start_date: string | null;
  date_label: string | null;
  adults: number | null;
  children: number | null;
  area: string | null;
  venue_name: string | null;
  phone: string | null;
  workbook_json: string | null;
  card_step: number | null;
  visit_evaluation: string | null;
  visit_tasted_on: string | null;
  last_comm_at: string | null;
  last_done_at: string | null;
};

export type FollowUpItem = {
  id: string;
  name: string;
  evaluation: EvaluationCode | null;
  evaluation_note: string | null;
  follow_up: string;
  /** True when the sheet had an evaluation but no date, so the date is tasting + EVLTN interval. */
  computed: boolean;
  overdue_days: number;
  event_date: string | null;
  venue: string | null;
  guests: number | null;
  phone: string | null;
  last_contact: string | null;
  card_step: number | null;
  sales_stage: string;
};

export type FollowUpList = { today: string; due: FollowUpItem[]; upcoming: FollowUpItem[]; count: number };

function isoDate(value: string | null | undefined): string | null {
  const text = (value ?? "").trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(text) ? text : null;
}

function bookOf(value: string | null): Record<string, string> {
  if (!value) return {};
  try {
    const parsed = JSON.parse(value) as unknown;
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const book: Record<string, string> = {};
    for (const [key, raw] of Object.entries(parsed as Record<string, unknown>)) {
      if (raw == null) continue;
      book[key] = String(raw);
    }
    return book;
  } catch {
    return {};
  }
}

function athensDate(timestamp: string | null): string | null {
  if (!timestamp) return null;
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) return isoDate(timestamp.slice(0, 10));
  return civilToday("Europe/Athens", date);
}

export function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** The next date after a call: today + the EVLTN interval. VLP, OTHER and no evaluation have no interval. */
export function nextFollowUp(evaluation: EvaluationCode | null, today: string): string | null {
  const days = evaluation ? FOLLOW_UP_DAYS[evaluation] : undefined;
  return days == null ? null : addDays(today, days);
}

export function evaluationOf(source: Pick<FollowUpSource, "visit_evaluation" | "workbook_json">): { code: EvaluationCode; note: string | null } | null {
  return parseEvaluation(source.visit_evaluation) ?? parseEvaluation(bookOf(source.workbook_json).evaluation);
}

/** null when the lead is closed or has no follow-up date (stored or computable). */
export function followUpEntry(source: FollowUpSource, today: string): FollowUpItem | null {
  if ((FOLLOW_UP_EXCLUDED_STAGES as readonly string[]).includes(source.sales_stage)) return null;
  const book = bookOf(source.workbook_json);
  const evaluation = evaluationOf(source);
  const code = evaluation?.code ?? null;
  const tastedOn = isoDate(source.visit_tasted_on) ?? isoDate(cellText("came", book.came ?? "")) ?? isoDate(cellText("visit_date", book.visit_date ?? ""));
  const stored = followUpFromWorkbook(book, code, tastedOn, today);
  let followUp = stored.follow_up;
  let computed = false;
  if (!followUp && !stored.follow_up_manual && code && tastedOn) {
    const days = FOLLOW_UP_DAYS[code];
    if (days != null) {
      followUp = addDays(tastedOn, days);
      computed = true;
    }
  }
  if (!followUp) return null;
  const contacts = [
    isoDate(cellText("mail_out", book.mail_out ?? "")),
    isoDate(cellText("mail_in", book.mail_in ?? "")),
    tastedOn,
    athensDate(source.last_comm_at),
    athensDate(source.last_done_at),
  ].filter((value): value is string => Boolean(value) && value! <= today);
  const lastContact = contacts.sort().at(-1) ?? null;
  const guests = source.adults == null && source.children == null ? null : (source.adults ?? 0) + (source.children ?? 0);
  return {
    id: source.id,
    name: source.title,
    evaluation: code,
    evaluation_note: evaluation?.note ?? null,
    follow_up: followUp,
    computed,
    overdue_days: followUp < today ? daysBetween(followUp, today) : 0,
    event_date: source.start_date ?? source.date_label ?? null,
    venue: source.venue_name ?? source.area ?? null,
    guests,
    phone: source.phone,
    last_contact: lastContact,
    card_step: source.card_step,
    sales_stage: source.sales_stage,
  };
}

/** TP > HP > MP > LP > VLP > OTHER > none, then the oldest date first. */
export function compareFollowUps(a: FollowUpItem, b: FollowUpItem): number {
  const rank = (item: FollowUpItem) => (item.evaluation ? RANK[item.evaluation] : 6);
  return rank(a) - rank(b) || a.follow_up.localeCompare(b.follow_up) || a.name.localeCompare(b.name, "el") || a.id.localeCompare(b.id);
}

export function groupFollowUps(sources: FollowUpSource[], today: string, horizon = FOLLOW_UP_HORIZON_DAYS): FollowUpList {
  const until = addDays(today, horizon);
  const due: FollowUpItem[] = [];
  const upcoming: FollowUpItem[] = [];
  for (const source of sources) {
    const item = followUpEntry(source, today);
    if (!item) continue;
    if (item.follow_up <= today) due.push(item);
    else if (item.follow_up <= until) upcoming.push(item);
  }
  due.sort(compareFollowUps);
  upcoming.sort(compareFollowUps);
  return { today, due, upcoming, count: due.length };
}

/** localStorage key for the once-a-day reminder. */
export function followUpPopupKey(userId: string, today: string): string {
  return `caterflow.followup-popup.${userId}.${today}`;
}
